import { useId, useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

/** Portée d'une même action (« Ce passage » / « Cette section ») : chacune a ses règles de consigne. */
export type InstructionScope = {
  description: string;
  instructionPlaceholder: string;
  instructionRequired: boolean;
  label: string;
  value: string;
};

type InstructionPopoverProps = {
  description?: string;
  disabled?: boolean;
  instructionLabel: string;
  instructionPlaceholder: string;
  /** L'instruction est obligatoire (reformulation) ou facultative (réécriture, insertion). */
  instructionRequired?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSubmit: (values: { instruction: string; scope: string | null; title: string }) => void;
  /** Portées proposées, la première est sélectionnée à l'ouverture ; elles remplacent description et placeholder. */
  scopes?: InstructionScope[];
  submitLabel: string;
  title: string;
  titleLabel?: string;
  titlePlaceholder?: string;
  trigger: ReactNode;
  /** Ajoute un champ de titre obligatoire (insertion d'une section). */
  withTitle?: boolean;
};

/** Petit formulaire flottant : une consigne en langage naturel (et un titre) avant de lancer une édition. */
export function InstructionPopover({
  description,
  disabled,
  instructionLabel,
  instructionPlaceholder,
  instructionRequired = false,
  onOpenChange,
  onSubmit,
  scopes,
  submitLabel,
  title,
  titleLabel,
  titlePlaceholder,
  trigger,
  withTitle = false,
}: InstructionPopoverProps) {
  const fieldId = useId();
  const [open, setOpen] = useState(false);
  const [instruction, setInstruction] = useState('');
  const [sectionTitle, setSectionTitle] = useState('');
  const [scopeValue, setScopeValue] = useState<string | null>(null);
  const scope = scopes?.find((candidate) => candidate.value === scopeValue) ?? scopes?.[0] ?? null;
  const isInstructionRequired = scope?.instructionRequired ?? instructionRequired;
  const canSubmit = !disabled
    && (!withTitle || sectionTitle.trim().length > 0)
    && (!isInstructionRequired || instruction.trim().length > 0);

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen);
    if (!nextOpen) {
      setScopeValue(null);
    }
    onOpenChange?.(nextOpen);
  }

  function submit() {
    if (!canSubmit) {
      return;
    }
    onSubmit({ instruction: instruction.trim(), scope: scope?.value ?? null, title: sectionTitle.trim() });
    setInstruction('');
    setSectionTitle('');
    handleOpenChange(false);
  }

  return (
    <Popover onOpenChange={handleOpenChange} open={open}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="end" aria-label={title} className="w-80 p-3" side="bottom">
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <div>
            <p className="text-sm font-semibold">{title}</p>
            {scope?.description ?? description
              ? <p className="mt-0.5 text-xs text-muted-foreground">{scope?.description ?? description}</p>
              : null}
          </div>
          {scopes && scopes.length > 1 ? (
            <div aria-label="Portée" className="grid grid-flow-col gap-1 rounded-lg bg-muted p-0.5" role="radiogroup">
              {scopes.map((candidate) => (
                <button
                  aria-checked={candidate.value === scope?.value}
                  className={cn(
                    'h-7 rounded-md px-2 text-xs font-medium text-muted-foreground transition-colors outline-none',
                    'hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50',
                    candidate.value === scope?.value && 'bg-background text-foreground shadow-sm',
                  )}
                  key={candidate.value}
                  onClick={() => setScopeValue(candidate.value)}
                  role="radio"
                  type="button"
                >
                  {candidate.label}
                </button>
              ))}
            </div>
          ) : null}
          {withTitle ? (
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium" htmlFor={`${fieldId}-title`}>{titleLabel}</label>
              <Input
                autoFocus
                className="h-9 text-sm"
                id={`${fieldId}-title`}
                maxLength={200}
                onChange={(event) => setSectionTitle(event.target.value)}
                placeholder={titlePlaceholder}
                value={sectionTitle}
              />
            </div>
          ) : null}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium" htmlFor={`${fieldId}-instruction`}>
              {instructionLabel}
              {isInstructionRequired ? null : <span className="font-normal text-muted-foreground"> (facultatif)</span>}
            </label>
            <Textarea
              autoFocus={!withTitle}
              className="min-h-20 py-2 text-sm"
              id={`${fieldId}-instruction`}
              maxLength={2_000}
              onChange={(event) => setInstruction(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                  event.preventDefault();
                  submit();
                }
              }}
              placeholder={scope?.instructionPlaceholder ?? instructionPlaceholder}
              value={instruction}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={() => handleOpenChange(false)} size="sm" type="button" variant="ghost">
              Annuler
            </Button>
            <Button disabled={!canSubmit} size="sm" type="submit">{submitLabel}</Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}
